/**
 * 반품조회 2.0 (rgr02, 라우트 rgr20) — 컴포넌트 모드 엔트리(문서 51 · 가이드 52).
 *
 * 화면은 `handoff/page.generated.tsx`(컨덕터 생성본 — 재핸드오프 때 통째로 교체)가 그리고, 이 파일은 그 뷰의 Wiring 슬롯에
 * 옛 배선(`hooks/useRgr02Screen` — API·검증·세션/URL 복원·화주 잠금)을 잇는다. UI 를 여기서 그리지 않는다.
 *
 * 규칙(가이드 52 §5-A): 실패 = throw(확인창·모달 유지), false = 성공인데 뷰 토스트 생략(훅이 결과 문구를 띄운다), search 의 false = 조회 안 함.
 *
 * 첨부·동영상: 셀을 누르면 뷰 모달 대신 옛 공용 MediaModal 을 연다(`cellOpens.<모달>` 이 false — pbo-design #657). 뷰의 DS 모달은 파일 이름만
 *  보여 주고(MediaFile = 이름·썸네일, URL 자리 없음) 파일을 열 수 없어서다. DS 모달이 URL 을 받게 되면 `media` 슬롯으로 돌아간다(PD 확인 2026-09-30).
 */
import { useMemo, useRef, useState } from 'react'
import { MediaModal } from '@/components/pbo/MediaModal'
import { invalidError, notice } from '@/components/pbo/notice'
import { guardFetching } from '@/hooks/useFetchAllRows'
import { buildShipperNameOptions } from '@/components/system/shipperSelectFilter'
import type { CoMingleReturnProps } from '@/pages/return/coMingle'
import { type ConfirmFn, useRgr02Screen } from './hooks/useRgr02Screen'
import {
  type HeadRow,
  type ItemRow,
  MESSAGES,
  ORD_TYPE_OPTIONS,
  REASON_DETAIL_MIN_LENGTH,
  type Rgr02FilterValues,
  filterRowsByKeyword,
  isValidDownloadPassword,
  pickList,
  summaryActionBlockReason,
} from './rules'
import { coreTableLayoutSlot } from '@/hooks/useCoreTableLayout'
import ReturnInquiryPageView, { type ActionCtx, type CellTarget, type Option, type Row, type Wiring } from './handoff/page.generated'
import { usePermissionSlot } from '@/hooks/useMenuPermission'

/** 표 그룹 id — 생성본이 `wiring.onSelect` 두 번째 인자로 주는 값(`<SelBridge scope="…" />`). 데모에서 그룹 노드 id 가 바뀌면 여기도 바꾼다. */
const HD_SCOPE = 'hd-tableGroup'
const IT_SCOPE = 'it-tableGroup'
/** 목록 검색어 상태 키(생성본 stateInitial/onState 계약). */
const HD_LISTQ = 'hd-table.q'
const IT_LISTQ = 'it-table.q'

/** 확인창은 뷰가 이미 띄웠다 — 훅의 confirm 자리에 넣어 곧바로 실행한다(훅은 옛 entry 때 확인창을 스스로 열던 계약). */
const runNow: ConfirmFn = (onOk) => onOk()
/** 옵션 슬롯이 매 렌더 불리므로 빈 목록은 참조를 고정한다(뷰의 filterFallback effect 가 옵션 배열 참조를 의존성으로 본다). */
const NO_OPTIONS: Option[] = []
const LOCKED_TYPE_OPTIONS = new Map<string, Option[]>()
/** 화주 고정 계정의 유형 옵션 한 줄 — 옵션 슬롯은 렌더마다 불리고 뷰 effect 가 배열 참조를 의존성으로 보므로 유형별로 참조를 고정한다 */
const lockedTypeOptions = (type: string): Option[] => {
  const cached = LOCKED_TYPE_OPTIONS.get(type) ?? [{ value: type, label: type }]
  LOCKED_TYPE_OPTIONS.set(type, cached)
  return cached
}

const asList = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : typeof v === 'string' && v ? [v] : [])
const asText = (v: unknown): string => (typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '')
const asRange = (v: unknown): [string, string] | undefined =>
  Array.isArray(v) && typeof v[0] === 'string' && typeof v[1] === 'string' ? [v[0], v[1]] : undefined

/** 뷰 조회영역 값 → 훅 조회 조건. 키는 생성본 filterKey = `Rgr02FilterValues` 키 그대로다('' 는 '전체'). */
const toFilterValues = (f: Record<string, unknown>): Rgr02FilterValues => ({
  service_type: asList(f.service_type),
  shipper_name: asList(f.shipper_name),
  PLN_DATE: asRange(f.PLN_DATE),
  ORD_OPT_NO: pickList(asList(f.ORD_OPT_NO)),
  ORD_TYPE: asText(f.ORD_TYPE) || undefined,
  ACT_DATE: asText(f.ACT_DATE) || undefined,
  ORD_NO: pickList(asList(f.ORD_NO)),
})

/** 뷰 행이 어느 표에서 왔는지 — ROWS/ROWS2 슬롯이 행에 `__src` 를 실어 둔다(ctx.selected 는 두 표가 공유하는 '마지막으로 바뀐 스코프' 값이라 모양으로 가른다). */
/** 목록 검색으로 숨긴 행을 뺀다 — 원본 행(source)에 검색어를 걸어 남은 키만 통과. 검색어가 없으면 그대로. */
const onlyListed = <T extends { key: string }>(rows: T[], source: { key: string }[], keyword: string): T[] => {
  if (!keyword.trim()) return rows
  const listed = new Set(filterRowsByKeyword(source, keyword).map((r) => r.key))
  return rows.filter((r) => listed.has(r.key))
}

const isHeadRow = (r: unknown): r is HeadRow => typeof r === 'object' && r !== null && (r as { __src?: unknown }).__src === 'head'
const isItemRow = (r: unknown): r is ItemRow => typeof r === 'object' && r !== null && (r as { __src?: unknown }).__src === 'item'

export default function ReturnInquiryPage({ coMingleEnabled = false }: Partial<CoMingleReturnProps>) {
  const screen = useRgr02Screen(coMingleEnabled)

  // 상단 행은 자기 key 를 마스터 pick 키(__pick)로, 하단 행은 지금 고른 상단 행의 key 를 실어 둔다 — 뷰의 상세 표는 `__pick` 이 pick 과 같은 행만 그린다.
  const headViewRows = useMemo<Row[]>(() => screen.headRows.map((r) => ({ ...r, __pick: r.key, __src: 'head' })), [screen.headRows])
  const itemViewRows = useMemo<Row[]>(
    () => screen.itemRows.map((r) => ({ ...r, __pick: screen.selectedHeadKey ?? '', __src: 'item' })),
    [screen.itemRows, screen.selectedHeadKey],
  )

  /** 표별 체크 선택(뷰의 onSelect, scope 로 가른다) — 액션의 ctx.selected 는 마지막으로 바뀐 스코프 하나라 이 값을 먼저 쓴다(가이드 52 §5-A 3). */
  const [selectedHeads, setSelectedHeads] = useState<HeadRow[]>([])
  const [selectedItems, setSelectedItems] = useState<ItemRow[]>([])
  /**
   * 표 위 목록 검색어(뷰 상태) — 엑셀 [열기] 가드가 "화면에 보이는 행 수" 를 세고, 처리 대상에서 검색으로 숨긴 행을 뺄 때 쓴다.
   * 옛 화면은 목록 검색 때 체크 선택을 비웠다(표가 dataSource 가 줄어도 선택을 풀지 않아서). 뷰는 목록 검색에 선택을 비우지 않으므로,
   * 숨긴 행이 입고취소·원복·재전송·저장 대상으로 나가지 않게 배선이 "지금 보이는 행" 으로 다시 거른다(사전 리뷰 Warning 2026-09-30).
   */
  const listqRef = useRef({ hd: '', it: '' })
  // 매 렌더 새로 만든다 — 슬롯이 최신 선택·행·검색어(ref)를 읽게.
  const headTargets = (ctx: ActionCtx): HeadRow[] =>
    onlyListed(selectedHeads.length ? selectedHeads : ctx.selected.filter(isHeadRow), screen.headRows, listqRef.current.hd)
  const itemTargets = (ctx: ActionCtx): ItemRow[] =>
    onlyListed(selectedItems.length ? selectedItems : ctx.selected.filter(isItemRow), screen.itemRows, listqRef.current.it)

  /** 요약·상세 어느 쪽이든 받는 중이면 안내하고 false — 곧 덮일 행으로 요청이 나가지 않게(LOGISPRD-15269). */
  const notFetching = () => guardFetching(screen.isHeadFetching || screen.isItemFetching)

  /** 원복·재전송(1행)을 막는 이유 — 확인창을 열기 전(opens)과 실행 직전(_ok)이 같은 판정을 쓴다. */
  const singleActionBlock = (rows: HeadRow[], action: 'restore' | 'resend'): string | null => {
    if (rows.length !== 1) return MESSAGES.singleRowOnly
    return summaryActionBlockReason(rows[0].raw, action)
  }

  /** 엑셀 다운로드 모달 확인 — 사유·상세·비밀번호 검증(실패 = throw, 모달 유지) 뒤 옛 훅의 다운로드. 대상(head/item)은 [열기] 가드가 훅에 미리 세워 둔다. */
  const runDownload = async (ctx: ActionCtx): Promise<false> => {
    const f = ctx.form ?? {}
    const reasonCode = asText(f.reason)
    const detailedReason = asText(f.detail).trim()
    const password = asText(f.password)
    if (!reasonCode || detailedReason.length < REASON_DETAIL_MIN_LENGTH) throw invalidError(MESSAGES.downloadValidation)
    if (!isValidDownloadPassword(password)) throw invalidError(MESSAGES.passwordInvalid)
    // 실패 안내는 useExcelDownload 가 띄운다.
    await screen.confirmDownload({ reasonCode, detailedReason, password })
    return false as const
  }
  /** 엑셀 [열기] 가드 — 목록 검색을 거쳐 화면에 보이는 행이 0이면 열지 않는다. 열면 훅에 대상을 세워 확인 슬롯이 그 표의 조건으로 내려받게 한다. */
  const openDownloadGate = (target: 'head' | 'item'): boolean => {
    if (!notFetching()) return false
    const rows = target === 'head' ? filterRowsByKeyword(screen.headRows, listqRef.current.hd) : filterRowsByKeyword(screen.itemRows, listqRef.current.it)
    if (rows.length === 0) {
      notice.invalid(MESSAGES.excelNoRows)
      return false
    }
    screen.openDownload(target, rows.length)
    return true
  }

  /** 첨부·동영상 셀 클릭 — 옛 공용 MediaModal(서버 파일 목록 + [열람])을 열고 뷰 모달은 열지 않는다(false). */
  const openOwnMedia = (kind: 'attach' | 'video', target: CellTarget): boolean => {
    if (isItemRow(target.row)) screen.openMedia(kind, target.row)
    return false
  }

  /**
   * 화주 고정 계정의 화주유형 — 화주 목록 응답 전에는 훅이 '전체'(['']) 를 주는데, 뷰의 filterFallback 은 "빈 배열" 일 때만 채운다(['']는 값으로 본다).
   * 그래서 뷰에 넘길 때 빈 유형은 []로(이미 알면 고른 화주의 유형으로) 바꿔, 목록이 온 뒤 fallback 이 그 유형을 채우게 한다(리뷰 지적 2026-09-30).
   */
  const forView = (f: Record<string, unknown>): Record<string, unknown> => {
    if (screen.isFreeShipper) return f
    const types = asList(f.service_type).filter(Boolean)
    if (types.length) return f
    const type = screen.lockedServiceTypeOf(asList(f.shipper_name))
    return { ...f, service_type: type ? [type] : [] }
  }

  // Wiring 은 매 렌더 새로 만든다 — 슬롯이 최신 상태·훅 값을 닫아 잡게(가이드 52).
  const wiring: Wiring = {
    permission: usePermissionSlot('rgr20'),
    // 개인 컬럼 배치 저장 — 메인 표만(모달 안 표는 저장하지 않는다)
    columnLayout: { rgr02HeadTable: coreTableLayoutSlot('RGR20_HEAD'), rgr02ItemTable: coreTableLayoutSlot('RGR20_ITEM') },
    rows: {
      // BE 가 전건을 주고 페이징은 클라이언트(뷰) — total 을 주지 않는다. 행 key 는 rules.toHeadRow/toItemRow 가 실은 유일 키다.
      ROWS: () => ({ rows: headViewRows, loading: screen.isHeadFetching }),
      ROWS2: () => ({ rows: itemViewRows, loading: screen.isItemFetching }),
    },
    actions: {
      /** [조회] — 조회영역 값을 옛 훅 조건으로. 검증에 걸리면 안내하고 false(뷰가 페이저·선택·pick 을 되돌리지 않는다). */
      search: (filters) => {
        const values = toFilterValues(filters)
        // 화주유형을 바꿔도 이미 고른 화주명이 칸에 남는다(뷰는 값을 옵션과 대조하지 않는다) — 그 조합은 항상 0건이라 조회 전에 막는다(gr02 준용).
        //  화주명을 고르는 잠금 계정(재고통합)은 훅이 유형을 고른 화주의 것으로 바꿔 보내므로 대조하지 않는다(LOGISPRD-15725).
        const types = pickList(values.service_type) ?? []
        const names = pickList(values.shipper_name) ?? []
        const typeFollowsShipper = !screen.isFreeShipper && screen.canSelectShipper
        if (!typeFollowsShipper && types.length && names.length && screen.shipperSources.length) {
          const allowed = new Set(buildShipperNameOptions(screen.shipperSources, { valueKey: 'shipperName', serviceTypes: types }).map((o) => String(o.value)))
          if (names.some((n) => !allowed.has(n))) {
            notice.invalid(MESSAGES.shipperNameNotInType)
            return false
          }
        }
        return screen.search(values) === false ? false : undefined
      },
      /** 하단 표 편집(특이사항) — 표가 준 전체 행을 훅이 키별로 합친다. */
      ROWS2_rowsChange: (rows) => screen.onItemRowsChange(rows.filter(isItemRow)),
      /** [입고취소] 확인 — 선택 행마다 순서대로(훅). 결과 문구는 훅이 띄우므로 false(뷰 토스트 생략). */
      rgr02CancelConfirm_ok: async (ctx) => {
        const rows = headTargets(ctx)
        const blocked = screen.cancelBlockReason(rows)
        if (blocked) throw invalidError(blocked)
        await screen.cancel(rows, runNow)
        return false as const
      },
      rgr02RestoreConfirm_ok: async (ctx) => {
        const rows = headTargets(ctx)
        const blocked = singleActionBlock(rows, 'restore')
        if (blocked) throw invalidError(blocked)
        await screen.restore(rows, runNow)
        return false as const
      },
      rgr02ResendConfirm_ok: async (ctx) => {
        const rows = headTargets(ctx)
        const blocked = singleActionBlock(rows, 'resend')
        if (blocked) throw invalidError(blocked)
        await screen.resend(rows, runNow)
        return false as const
      },
      /** [저장] 확인 — 체크한 하단 행의 특이사항만 PATCH(훅). */
      rgr02SaveConfirm_ok: async (ctx) => {
        const rows = itemTargets(ctx)
        if (!screen.saveTargets(rows).length) throw invalidError(MESSAGES.saveNoTarget)
        await screen.save(rows, runNow)
        return false as const
      },
      rgr02HeadDownloadModal_ok: runDownload,
      rgr02ItemDownloadModal_ok: runDownload,
      // 첨부·동영상 모달은 보기 전용(저장본 hideOk) — 확인이 불려도 셀에 되돌릴 것이 없다(패치 없음, 토스트 없음).
      rgr02VideoModal_ok: () => false,
      rgr02AttachModal_ok: () => false,
    },
    opens: {
      hdCancelButton: (ctx) => {
        if (!notFetching()) return false
        const reason = screen.cancelBlockReason(headTargets(ctx))
        if (reason) notice.invalid(reason)
        return !reason
      },
      // 원복·재전송은 1행 선택에서만(LOGISPRD-12754 결정) — 뷰 버튼은 선택 0개만 막으므로 여기서 1행·가능 상태를 본다.
      hdRestoreButton: (ctx) => {
        if (!notFetching()) return false
        const reason = singleActionBlock(headTargets(ctx), 'restore')
        if (reason) notice.invalid(reason)
        return !reason
      },
      hdResendButton: (ctx) => {
        if (!notFetching()) return false
        const reason = singleActionBlock(headTargets(ctx), 'resend')
        if (reason) notice.invalid(reason)
        return !reason
      },
      hdExcelButton: () => openDownloadGate('head'),
      itExcelButton: () => openDownloadGate('item'),
      itSaveButton: (ctx) => {
        if (!notFetching()) return false
        if (screen.saveTargets(itemTargets(ctx)).length) return true
        notice.invalid(MESSAGES.saveNoTarget)
        return false as const
      },
    },
    options: {
      // 화주 고정 계정은 고른 화주의 유형 하나만(gr02 준용) — 화주 목록이 오기 전엔 빈 목록(데모 6종을 잠깐이라도 보이지 않게). 통합 운영(MUSINSA)은 데모 6종 그대로.
      RGR02SHIPPER_TYPE_OPTIONS: (demo, filters) => {
        if (screen.isFreeShipper) return demo
        const type = screen.lockedServiceTypeOf(asList(filters?.shipper_name))
        return type ? lockedTypeOptions(type) : NO_OPTIONS
      },
      // 화주명은 화주 목록 API(계정 스코프 적용)에서 — 현재 고른 화주유형으로 좁힌다.
      //  화주 고정 계정은 유형으로 좁히지 않는다 — 유형 칸이 고른 화주를 따르므로 좁히면 다른 화주(MUSINSA)를 고를 수 없다(gr02V2 준용).
      RGR02SHIPPER_NAME_OPTIONS: (_demo, filters) =>
        buildShipperNameOptions(screen.shipperSources, {
          valueKey: 'shipperName',
          serviceTypes: screen.isFreeShipper ? (pickList(asList(filters?.service_type)) ?? []) : [],
        }) as Option[],
      RGR02ORDTYPE_OPTIONS: () => ORD_TYPE_OPTIONS,
      // 엑셀 사유코드(HEAD·ITEM 두 모달) — 1.0 공용 콤보(DOWNLOAD_CD). 콤보가 비면(로딩·실패) 빈 목록 — 없는 코드를 고르게 두는 쪽이 더 나쁘다.
      REASONRADIO_OPTIONS: () => screen.reasonOptions as Option[],
      REASONRADIO_OPTIONS2: () => screen.reasonOptions as Option[],
    },
    /** 조회 조건 기본값 — 계정 기준(MUSINSA = M1P/MUSINSA · 화주 고정 계정 = 자기 유형·화주) + 입고예정일자 오늘(훅 buildDefaultFilter). */
    filterDefaults: { rgr02FilterCard: (demo) => forView({ ...demo, ...screen.defaultFilter }) },
    /**
     * 화주 고정 계정은 화주유형·화주명 칸을 잠근다(1.0 FilterBar 의 disabled, LOGISPRD-14949). 보이는 것만 잠그므로 값은 기본값·fallback·훅 search 가 따로 고정한다.
     *  재고통합 ON 의 29CM·INOOINT 는 화주명만 연다 — 자기 화주와 MUSINSA 를 고른다(LOGISPRD-15725)
     */
    filterDisabled: screen.isFreeShipper ? undefined : { service_type: true, shipper_name: !screen.canSelectShipper },
    /** 진입 초기값 — URL 복원 조건이 있으면 기본값 위에 얹는다(훅이 진입 즉시 그 조건으로 한 번 조회한다). */
    filterInitial: { rgr02FilterCard: (defaults) => forView({ ...defaults, ...(screen.restoredFilter ?? {}) }) },
    /** 화주 고정 계정의 화주유형은 화주 목록 응답 뒤에야 정해진다 — 비어 있을 때만 고른 화주의 유형으로 채운다. */
    filterFallback: {
      rgr02FilterCard: (current) => {
        const type = screen.isFreeShipper ? undefined : screen.lockedServiceTypeOf(asList(current.shipper_name))
        return type && asList(current.service_type).filter(Boolean).length === 0 ? { service_type: [type] } : undefined
      },
    },
    /** 상단 행 클릭(pick) = 마스터 — 그 행 키로 하단 상세를 읽는다. 빈 배열 = 해제([조회]·[초기화]·확인창 뒷정리). */
    onPick: (rows) => {
      const r = rows[0]
      if (!r) return screen.clearHeadPick()
      if (!isHeadRow(r)) return undefined
      return screen.selectHeadRow(r)
    },
    onSelect: (rows, scope) => {
      if (scope === HD_SCOPE) setSelectedHeads(rows.filter(isHeadRow))
      else if (scope === IT_SCOPE) setSelectedItems(rows.filter(isItemRow))
      // 모르는 scope 면 아무것도 지우지 않는다 — 선택을 푼 신호는 rows 가 빈 배열이라 행만 보고는 어느 표인지 알 수 없다.
    },
    cellOpens: {
      rgr02AttachModal: (target) => openOwnMedia('attach', target),
      rgr02VideoModal: (target) => openOwnMedia('video', target),
    },
    /** 다운로드 모달이 둘 다 닫히면 훅의 대상도 비운다(취소로 닫았을 때). */
    onModal: (open) => {
      if (!open.rgr02HeadDownloadModal && !open.rgr02ItemDownloadModal) screen.closeDownload()
    },
    onState: (s) => {
      listqRef.current = { hd: s.listq[HD_LISTQ], it: s.listq[IT_LISTQ] }
    },
    /** [초기화] — 저장된 조회 조건(세션·URL)과 두 표·선택을 모두 비운다(훅 reset). 조회영역 값은 뷰가 filterDefaults 로 되돌린다. */
    onReset: () => {
      screen.reset()
      setSelectedHeads([])
      setSelectedItems([])
    },
  }

  // 타이틀 행·조회영역·표·모달은 뷰가 그린다 — 여기서 또 그리면 제목이 두 번 선다. 첨부·동영상만 옛 공용 모달(열람 가능)을 옆에 둔다.
  return (
    <>
      <ReturnInquiryPageView wiring={wiring} />
      <MediaModal
        open={Boolean(screen.mediaTarget)}
        kind={screen.mediaTarget?.kind ?? null}
        row={screen.mediaTarget?.row ?? null}
        shipper={screen.itemShipperCode}
        filesByFileId={screen.filesByFileId}
        readOnly
        onClose={screen.closeMedia}
      />
    </>
  )
}
